import React from 'react';
import type { AlaCarteContent, AlaCarteItem, AlaCarteSection } from '@core/interfaces/api';
import {
  ALACARTE_COLUMN_FLEX,
  LEFT_ALIGNED_MENU_DESCRIPTION_STYLE,
  MenuLogo,
  FitToPage,
} from '../shared/menu-print-shared';

export interface AlaCarteTemplateProps {
  communityName: string;
  title: string;
  bugImageUrl?: string | null;
  content: AlaCarteContent;
}

function AlaCarteLeftSection({ section }: { section: AlaCarteSection }) {
  return (
    <div>
      <div style={{
        fontSize: '28px',
        fontFamily: '"Times New Roman", Times, serif',
        textAlign: 'center',
        marginBottom: '6px',
        lineHeight: '1.2',
      }}>
        {section.header}
      </div>
      {section.items.map((item: AlaCarteItem) => (
        <div key={item.id} style={{
          fontStyle: 'italic',
          fontSize: '18px',
          fontFamily: 'Arial, Helvetica, sans-serif',
          color: '#222',
          textAlign: 'center',
          lineHeight: '1.4',
          whiteSpace: 'pre-line',
        }}>
          {item.name}
        </div>
      ))}
    </div>
  );
}

function AlaCarteRightSection({ section }: { section: AlaCarteSection }) {
  return (
    <div style={{ marginBottom: '28px' }}>
      <div style={{
        fontSize: '34px',
        fontFamily: '"Times New Roman", Times, serif',
        textAlign: 'left',
        marginBottom: '10px',
        lineHeight: '1.2',
      }}>
        {section.header}
      </div>
      {section.items.map((item: AlaCarteItem) => {
        const hasDesc = !!item.description?.trim();
        return (
          <div key={item.id} style={{ marginBottom: hasDesc ? '14px' : '2px' }}>
            {hasDesc ? (
              <>
                <div style={{
                  fontWeight: 'bold',
                  fontSize: '17px',
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                }}>
                  {item.name}
                </div>
                <div style={{
                  ...LEFT_ALIGNED_MENU_DESCRIPTION_STYLE,
                  fontStyle: 'italic',
                  fontSize: '17px',
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  color: '#333',
                  lineHeight: '1.4',
                  marginTop: '1px',
                }}>
                  {item.description}
                </div>
              </>
            ) : (
              <div style={{
                fontStyle: 'italic',
                fontSize: '18px',
                fontFamily: 'Arial, Helvetica, sans-serif',
                color: '#222',
                textAlign: 'left',
                lineHeight: '1.4',
                whiteSpace: 'pre-line',
              }}>
                {item.name}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function AlaCarteTemplate({ communityName, title, bugImageUrl, content }: AlaCarteTemplateProps) {
  const logo = (
    <div style={{ textAlign: 'center', marginBottom: '28px' }}>
      {bugImageUrl && <MenuLogo bugImageUrl={bugImageUrl} />}
      {title && (
        <div style={{
          fontSize: '26px',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          marginTop: '4px',
        }}>
          {title}
        </div>
      )}
    </div>
  );

  return (
    <FitToPage fixedContent={logo} showBorder>
      {(() => {
        const layout = content.columnLayout ?? '1/3-2/3';
        const [leftFlex, rightFlex] = ALACARTE_COLUMN_FLEX[layout] ?? ALACARTE_COLUMN_FLEX['1/3-2/3'];
        return (
          <div style={{ display: 'flex', flex: 1, marginTop: '16px' }}>
            <div style={{ flex: leftFlex, paddingRight: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
              {content.leftColumn.map((section) => (
                <AlaCarteLeftSection key={section.id} section={section} />
              ))}
            </div>
            <div style={{ flex: rightFlex, paddingLeft: '14px' }}>
              {content.rightColumn.map((section) => (
                <AlaCarteRightSection key={section.id} section={section} />
              ))}
            </div>
          </div>
        );
      })()}
    </FitToPage>
  );
}
